import { lazy, Suspense } from 'react';
import { Routes, Route } from 'react-router-dom';
import RequireAuth from './components/RequireAuth';
import AppShell from './components/layout/AppShell';
import Spinner from './components/ui/Spinner';
import LoginPage from './pages/LoginPage';
import AuthGoogleCallback from './pages/AuthGoogleCallback';
import AuthMicrosoftCallback from './pages/AuthMicrosoftCallback';
import Dashboard from './pages/Dashboard';
import LecturePage from './pages/LecturePage';
import StudyPage from './pages/StudyPage';
import Settings from './pages/Settings';
import NotFound from './pages/NotFound';
import ChangePasswordPage from './pages/ChangePasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import AnonAttendancePage from './pages/AnonAttendancePage';
import ClassPage from './pages/ClassPage';
import ProjectionPage from './pages/ProjectionPage';
import SurveyPage from './pages/SurveyPage';
import MyTestimonialsPage from './pages/MyTestimonialsPage';
import LearnerPlanPage from './pages/LearnerPlanPage';
import MyRecordPage from './pages/MyRecordPage';
import ProgressReportPage from './pages/ProgressReportPage';

// WIP-008 (bundle splitting) — route-lazy surfaces:
//   • /teach/* and /platform-admin/* ship to every student today even though
//     only instructors/admins can ever open them (the backend's requireInstructor
//     / admin middleware is the real gate; this is purely a download-weight cut).
//   • /lab and /lab/:experimentId pull in the full Virtual Lab widget
//     catalogue (WidgetRenderer + every widget cluster), which most lessons
//     never reference.
//   • /flashcards and /glossary each carry a large, self-contained content
//     dataset (frontend/src/data/flashcards.ts and data/glossary.ts) that
//     nothing else in the app reads — a student opening a lesson pays nothing
//     for either unless they visit those pages.
// Deliberately NOT lazy: Dashboard, LecturePage, ClassPage, ProjectionPage — the
// lesson player and the live Class panel must never show a loading state (see
// RouteFallback below), and Dashboard is the first screen after login.
const Teach = lazy(() => import('./pages/Teach'));
const PlatformAdmin = lazy(() => import('./pages/PlatformAdmin'));
const VirtualLab = lazy(() => import('./pages/VirtualLab'));
const LabPage = lazy(() => import('./pages/LabPage'));
const FlashcardsPage = lazy(() => import('./pages/FlashcardsPage'));
const GlossaryPage = lazy(() => import('./pages/GlossaryPage'));

// Full-height centered fallback for a lazy route boundary. Only ever used for
// the four routes above — never for the lesson player or the live Class panel.
function RouteFallback() {
  return (
    <div className="flex h-full min-h-[50vh] items-center justify-center">
      <Spinner size={22} label="Loading…" />
    </div>
  );
}

// Route map. Public: /login, the two OAuth callbacks, and /reset (BP 26 —
// password reset must be reachable with no session at all). /account/password
// is authenticated but deliberately OUTSIDE the AppShell: a student who must
// change their password before continuing should not depend on the shell's
// own /auth/me rehydration succeeding first. Everything else lives behind
// RequireAuth inside the AppShell (which provides the Outlet).
export default function AppRoutes() {
  return (
    <Routes>
      {/* Public */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/auth/google/callback" element={<AuthGoogleCallback />} />
      <Route path="/auth/microsoft/callback" element={<AuthMicrosoftCallback />} />
      <Route path="/reset" element={<ResetPasswordPage />} />
      {/* PUBLIC — no auth, deliberately outside the shell. Students check in here
          while student-ID login is off. */}
      <Route path="/a" element={<AnonAttendancePage />} />

      {/* Authenticated, outside the shell */}
      <Route
        path="/account/password"
        element={
          <RequireAuth>
            <ChangePasswordPage />
          </RequireAuth>
        }
      />

      {/* WIP-011 (design §2.1) — the projector: a second browser window, often
          a second device, that carries no TopBar/LeftNav/Teach chrome. */}
      <Route
        path="/project/:sessionId"
        element={
          <RequireAuth>
            <ProjectionPage />
          </RequireAuth>
        }
      />

      {/* The printable progress report (BP 25/27). Outside the shell for the
          same reason the projector is: a page carrying no TopBar/LeftNav/Teach
          chrome prints as the document and nothing else, which is sturdier
          than hiding the chrome at print time and finding out it was clipped
          only once a printout came out cropped. `:userId` may be `me`. */}
      <Route
        path="/reports/learner-plan/:userId"
        element={
          <RequireAuth>
            <ProgressReportPage />
          </RequireAuth>
        }
      />

      {/* Authenticated — wrapped in the shell */}
      <Route
        element={
          <RequireAuth>
            <AppShell />
          </RequireAuth>
        }
      >
        <Route path="/" element={<Dashboard />} />
        <Route path="/courses/:courseId" element={<Dashboard />} />
        <Route path="/lessons/:lessonId" element={<LecturePage />} />
        <Route
          path="/lab"
          element={
            <Suspense fallback={<RouteFallback />}>
              <VirtualLab />
            </Suspense>
          }
        />
        <Route
          path="/lab/:experimentId"
          element={
            <Suspense fallback={<RouteFallback />}>
              <LabPage />
            </Suspense>
          }
        />
        <Route
          path="/glossary"
          element={
            <Suspense fallback={<RouteFallback />}>
              <GlossaryPage />
            </Suspense>
          }
        />
        <Route path="/study" element={<StudyPage />} />
        <Route
          path="/flashcards"
          element={
            <Suspense fallback={<RouteFallback />}>
              <FlashcardsPage />
            </Suspense>
          }
        />
        <Route path="/settings" element={<Settings />} />
        <Route
          path="/platform-admin/*"
          element={
            <Suspense fallback={<RouteFallback />}>
              <PlatformAdmin />
            </Suspense>
          }
        />
        <Route path="/class" element={<ClassPage />} />
        <Route path="/surveys/:key" element={<SurveyPage />} />
        <Route path="/me/testimonials" element={<MyTestimonialsPage />} />
        <Route path="/learner-plan" element={<LearnerPlanPage />} />
        <Route path="/me/record" element={<MyRecordPage />} />
        <Route
          path="/teach/*"
          element={
            <Suspense fallback={<RouteFallback />}>
              <Teach />
            </Suspense>
          }
        />
        <Route path="*" element={<NotFound />} />
      </Route>
    </Routes>
  );
}
